import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/password-reset/request`, { email });
      setSubmitted(true);
      toast.success('Password reset request submitted');
    } catch (error) {
      console.error('Password reset request error:', error);
      // Still show success message for security (don't reveal if email exists)
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Request Submitted</h2>
          <p className="text-gray-600 mb-6">
            If an account exists with <strong>{email}</strong>, your password reset request has been sent to the administrator.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            You will receive an email with a password reset link once the administrator approves your request. This typically happens within a few hours during business hours.
          </p>
          <Link to="/login" className="btn btn-primary w-full flex items-center justify-center space-x-2">
            <ArrowLeft size={18} />
            <span>Back to Login</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="text-primary-600" size={32} />
          </div>
          <h2 className="text-3xl font-bold mb-2">Forgot Password?</h2>
          <p className="text-gray-600">
            Enter your email address and we'll send a reset request to the administrator.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="your.email@example.com"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? 'Submitting...' : 'Request Password Reset'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center space-x-1">
            <ArrowLeft size={16} />
            <span>Back to Login</span>
          </Link>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Your password reset request will be sent to the administrator for approval. Once approved, you'll receive an email with a secure link to reset your password.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
